import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    FormGroup,
    FormControl,
    Glyphicon,
    Label,
    Table,
} from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
    getCapsAdminOnly,
    getAllBuilders,
    getDataDeletionLastHealthDates,
    refreshCapS3DataCounts,
    patchCapStatus,
} from "../utils/bedrockAPI";
import { getUser } from "../utils/Common";
import "./DataDeletion.css";
import "./Admin.css";

function formatDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleString();
}

function formatDateShort(d) {
    if (!d) return null;
    const date = new Date(d);
    return isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

function formatBytes(n) {
    if (typeof n !== "number" || !isFinite(n) || n < 0) return null;
    if (n < 1024) return `${n} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let v = n;
    let i = -1;
    do {
        v /= 1024;
        i += 1;
    } while (v >= 1024 && i < units.length - 1);
    return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function daysSince(d) {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
}

function getDataCounts(cap, kind) {
    const o = cap?.updates?.data?.[kind];
    if (!o || typeof o !== "object") return null;
    const inS3 = typeof o.IN_S3 === "number" ? o.IN_S3 : null;
    const archived = typeof o.ARCHIVED === "number" ? o.ARCHIVED : null;
    const deleted = typeof o.DELETED === "number" ? o.DELETED : null;
    if (inS3 == null && archived == null && deleted == null) return null;
    return { inS3, archived, deleted };
}

function getS3Count(cap, kind) {
    const counts = getDataCounts(cap, kind);
    return counts ? counts.inS3 : null;
}

function getDeletionInfo(cap) {
    const d = cap?.updates?.data?.deletion;
    if (!d || typeof d !== "object") return null;
    return d;
}

function deletionStatusBsStyle(status) {
    const s = (status || "").toUpperCase();
    if (s === "COMPLETE" || s === "COMPLETED" || s === "DONE") return "success";
    if (s === "FAILED" || s === "ERROR") return "danger";
    if (s === "RUNNING" || s === "IN_PROGRESS" || s === "STARTED" || s === "PENDING") return "warning";
    return "info";
}

function formatCountCell(counts) {
    if (!counts) return null;
    const parts = [];
    if (counts.archived != null && counts.archived > 0) parts.push(`A ${counts.archived.toLocaleString()}`);
    if (counts.deleted != null && counts.deleted > 0) parts.push(`D ${counts.deleted.toLocaleString()}`);
    return parts;
}

function capDisplayName(cap) {
    if (!cap) return "";
    if (cap.neighborhood && cap.name) return `${cap.neighborhood}, ${cap.name}`;
    return cap.name || cap.capId || "";
}

const SORT_KEYS = {
    name: "name",
    builder: "builder",
    lastHealth: "lastHealth",
    events: "events",
    images: "images",
    status: "status",
};

export default function DataDeletion(props) {
    const { history, setShowError, setErrorMessage, setErrorStatus } = props;
    const [caps, setCaps] = useState(null);
    const [activeByBuilder, setActiveByBuilder] = useState({});
    const [buildersById, setBuildersById] = useState({});
    const [lastHealthByCap, setLastHealthByCap] = useState({});
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [hideDelete, setHideDelete] = useState(true);
    const [sortKey, setSortKey] = useState(SORT_KEYS.lastHealth);
    const [sortAsc, setSortAsc] = useState(true);
    const [countingCapId, setCountingCapId] = useState(null);
    const [deletingCapId, setDeletingCapId] = useState(null);

    const loadData = async (forceRefresh = false) => {
        if (!forceRefresh && (hasLoaded || loading)) return;
        setLoading(true);
        try {
            const [decomList, activeList, buildersList, healthMap] = await Promise.all([
                getCapsAdminOnly(["DECOMISSIONED", "DELETE"]),
                getCapsAdminOnly(["ACTIVE"]),
                getAllBuilders(),
                getDataDeletionLastHealthDates(),
            ]);

            const rawCaps = Array.isArray(decomList) ? decomList : [];
            setCaps(rawCaps);

            const builderMap = {};
            (Array.isArray(buildersList) ? buildersList : []).forEach((b) => {
                if (b && b.builderId) builderMap[b.builderId] = b;
            });
            setBuildersById(builderMap);

            const activeMap = {};
            (Array.isArray(activeList) ? activeList : []).forEach((c) => {
                if (!c || !c.builderId) return;
                if (!activeMap[c.builderId]) activeMap[c.builderId] = [];
                activeMap[c.builderId].push(c);
            });
            Object.keys(activeMap).forEach((id) => {
                activeMap[id].sort((a, b) =>
                    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
                );
            });
            setActiveByBuilder(activeMap);
            setLastHealthByCap(healthMap && typeof healthMap === "object" ? healthMap : {});
        } catch (error) {
            console.error("Error loading data deletion list:", error);
            setCaps([]);
            if (setShowError) {
                setShowError(true);
                if (setErrorMessage) setErrorMessage("Failed to load data deletion list.");
                if (setErrorStatus) setErrorStatus("error-message");
            }
        }
        setHasLoaded(true);
        setLoading(false);
    };

    useEffect(() => {
        if (getUser().role !== "ADMIN") {
            history.push("/dashboard");
            return;
        }
        loadData();
    }, []);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortAsc((prev) => !prev);
        } else {
            setSortKey(key);
            setSortAsc(key === SORT_KEYS.name || key === SORT_KEYS.builder);
        }
    };

    const sortIcon = (key) => {
        if (sortKey !== key) return null;
        return (
            <Glyphicon
                glyph={sortAsc ? "chevron-up" : "chevron-down"}
                style={{ marginLeft: 4, fontSize: 10 }}
            />
        );
    };

    const getSortValue = (cap, key) => {
        switch (key) {
            case SORT_KEYS.name:
                return (cap.name || cap.capId || "").toLowerCase();
            case SORT_KEYS.builder: {
                const b = buildersById[cap.builderId];
                return (b?.name || "").toLowerCase();
            }
            case SORT_KEYS.lastHealth: {
                const d = lastHealthByCap[cap.capId];
                return d ? new Date(d).getTime() : 0;
            }
            case SORT_KEYS.events: {
                const n = getS3Count(cap, "events");
                return n == null ? -1 : n;
            }
            case SORT_KEYS.images: {
                const n = getS3Count(cap, "images");
                return n == null ? -1 : n;
            }
            case SORT_KEYS.status: {
                const del = getDeletionInfo(cap);
                const delStatus = del?.status ? String(del.status).toLowerCase() : "";
                return `${(cap.status || "").toLowerCase()} ${delStatus}`.trim();
            }
            default:
                return "";
        }
    };

    const handleFetchCounts = async (cap) => {
        if (!cap?.capId || countingCapId) return;
        setCountingCapId(cap.capId);
        try {
            const result = await refreshCapS3DataCounts(cap.capId);
            setCaps((prev) =>
                prev
                    ? prev.map((c) =>
                          c.capId === cap.capId
                              ? { ...c, updates: result.updates || c.updates }
                              : c
                      )
                    : prev
            );
        } catch (error) {
            console.error("Error fetching S3 counts:", error);
            if (setShowError) {
                setShowError(true);
                if (setErrorMessage) {
                    setErrorMessage(
                        "Failed to fetch S3 counts for " + (cap.name || cap.capId) + ". This can take a while — try again."
                    );
                }
                if (setErrorStatus) setErrorStatus("error-message");
            }
        } finally {
            setCountingCapId(null);
        }
    };

    const handleMarkDelete = async (cap) => {
        if (!cap?.capId || deletingCapId) return;
        const label = capDisplayName(cap) || cap.capId;
        const confirmed = window.confirm(
            `Mark "${label}" as DELETE?\n\n` +
                "This flags the cap for data deletion. Backend archival will handle the rest.\n\n" +
                "Are you sure?"
        );
        if (!confirmed) return;

        setDeletingCapId(cap.capId);
        try {
            await patchCapStatus(cap.capId, "DELETE");
            setCaps((prev) =>
                prev
                    ? prev.map((c) => (c.capId === cap.capId ? { ...c, status: "DELETE" } : c))
                    : prev
            );
        } catch (error) {
            console.error("Error marking cap DELETE:", error);
            if (setShowError) {
                setShowError(true);
                if (setErrorMessage) {
                    setErrorMessage("Failed to mark " + (cap.name || cap.capId) + " as DELETE.");
                }
                if (setErrorStatus) setErrorStatus("error-message");
            }
        } finally {
            setDeletingCapId(null);
        }
    };

    const term = searchTerm.trim().toLowerCase();
    const filteredCaps = (caps || [])
        .filter((cap) => {
            if (hideDelete && cap.status === "DELETE") return false;
            if (!term) return true;
            const builder = buildersById[cap.builderId];
            const hay = [
                cap.name,
                cap.capId,
                cap.status,
                builder?.name,
                ...(activeByBuilder[cap.builderId] || []).map((c) => c.name),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(term);
        })
        .sort((a, b) => {
            const av = getSortValue(a, sortKey);
            const bv = getSortValue(b, sortKey);
            let cmp = 0;
            if (typeof av === "number" && typeof bv === "number") {
                cmp = av - bv;
            } else {
                cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
            }
            return sortAsc ? cmp : -cmp;
        });

    const header = (
        <header className="admin-header">
            <nav className="brand-breadcrumb">
                <Link to="/admin">Admin</Link>
                <span className="brand-breadcrumb-sep">/</span>
                <span className="brand-breadcrumb-current">Data deletion</span>
            </nav>
            <h1 className="admin-title">Data deletion</h1>
            {/* <p className="data-deletion-subtitle">
                DECOMISSIONED caps — find units out of commission long enough to safely delete stored data.
            </p> */}
        </header>
    );

    return (
        <div className="data-deletion-page admin-page">
            {header}
            <div className="data-deletion-container">
                <div className="data-deletion-search">
                    <FormGroup>
                        <div className="data-deletion-search-row">
                            <FormControl
                                type="text"
                                placeholder="Search by cap name, builder, or cap ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="data-deletion-search-input"
                            />
                            <Button
                                bsStyle="default"
                                onClick={() => setSearchTerm("")}
                                className="data-deletion-icon-button"
                                title="Clear search"
                            >
                                <Glyphicon glyph="remove" />
                            </Button>
                            <Button
                                bsStyle="default"
                                onClick={() => loadData(true)}
                                disabled={loading}
                                className="data-deletion-icon-button"
                                title="Refresh"
                            >
                                <Glyphicon glyph="refresh" className={loading ? "glyphicon-spin" : ""} />
                            </Button>
                        </div>
                        <div className="data-deletion-filters">
                            <Button
                                bsSize="small"
                                bsStyle={hideDelete ? "primary" : "default"}
                                onClick={() => setHideDelete((prev) => !prev)}
                                title={hideDelete ? "Show DELETE status caps" : "Hide DELETE status caps"}
                            >
                                {hideDelete ? "Hiding DELETE" : "Showing DELETE"}
                            </Button>
                        </div>
                    </FormGroup>
                </div>

                {hasLoaded && (
                    <div className="data-deletion-count">
                        Caps: {filteredCaps.length} of {caps ? caps.length : 0}
                    </div>
                )}

                {loading ? (
                    <div className="data-deletion-loading">
                        <Glyphicon glyph="refresh" className="glyphicon-spin" style={{ fontSize: 24, marginRight: 8 }} />
                        Loading decommissioned caps...
                    </div>
                ) : !caps ? (
                    <div className="data-deletion-empty">No data yet.</div>
                ) : filteredCaps.length === 0 ? (
                    <div className="data-deletion-empty">No caps match the current filters.</div>
                ) : (
                    <div className="data-deletion-table-wrapper">
                        <Table responsive striped bordered hover className="data-deletion-table">
                            <thead>
                                <tr>
                                    <th className="data-deletion-copy-th"></th>
                                    <th className="data-deletion-th-sort" onClick={() => toggleSort(SORT_KEYS.name)}>
                                        Cap {sortIcon(SORT_KEYS.name)}
                                    </th>
                                    <th className="data-deletion-th-sort data-deletion-builder-th" onClick={() => toggleSort(SORT_KEYS.builder)}>
                                        Builder {sortIcon(SORT_KEYS.builder)}
                                    </th>
                                    <th className="data-deletion-active-th">Other caps</th>
                                    <th className="data-deletion-th-sort" onClick={() => toggleSort(SORT_KEYS.lastHealth)}>
                                        Last health {sortIcon(SORT_KEYS.lastHealth)}
                                    </th>
                                    <th className="data-deletion-th-sort" onClick={() => toggleSort(SORT_KEYS.events)}>
                                        Events {sortIcon(SORT_KEYS.events)}
                                    </th>
                                    <th className="data-deletion-th-sort" onClick={() => toggleSort(SORT_KEYS.images)}>
                                        Images {sortIcon(SORT_KEYS.images)}
                                    </th>
                                    <th className="data-deletion-th-sort" onClick={() => toggleSort(SORT_KEYS.status)}>
                                        Status {sortIcon(SORT_KEYS.status)}
                                    </th>
                                    <th className="data-deletion-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCaps.map((cap) => {
                                    const builder = buildersById[cap.builderId];
                                    const builderName = builder?.name || (cap.builderId ? "—" : "No builder");
                                    const activeOthers = activeByBuilder[cap.builderId] || [];
                                    const lastHealth = lastHealthByCap[cap.capId];
                                    const ageDays = daysSince(lastHealth);
                                    const eventCounts = getDataCounts(cap, "events");
                                    const imageCounts = getDataCounts(cap, "images");
                                    const eventBreakdown = formatCountCell(eventCounts);
                                    const imageBreakdown = formatCountCell(imageCounts);
                                    const deletion = getDeletionInfo(cap);
                                    const isCounting = countingCapId === cap.capId;
                                    const isDeleting = deletingCapId === cap.capId;
                                    const isDelete = cap.status === "DELETE";
                                    const deletionTooltip = deletion
                                        ? [
                                              deletion.status && `Status: ${deletion.status}`,
                                              deletion.startedAt && `Started: ${formatDate(deletion.startedAt)}`,
                                              deletion.completedAt && `Completed: ${formatDate(deletion.completedAt)}`,
                                              typeof deletion.archiveAmount === "number" &&
                                                  `Archived bytes: ${deletion.archiveAmount.toLocaleString()}`,
                                          ]
                                              .filter(Boolean)
                                              .join("\n")
                                        : undefined;

                                    return (
                                        <tr key={cap.capId} className={isDelete ? "data-deletion-row-delete" : undefined}>
                                            <td className="data-deletion-copy-cell">
                                                {cap.capId ? (
                                                    <CopyToClipboard text={cap.capId}>
                                                        <Button
                                                            bsSize="xsmall"
                                                            bsStyle="default"
                                                            title="Copy cap ID"
                                                        >
                                                            <Glyphicon glyph="duplicate" />
                                                        </Button>
                                                    </CopyToClipboard>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td className="data-deletion-cap-cell">
                                                <Link to={`/caps/${cap.capId}`} className="data-deletion-cap-link">
                                                    {cap.name || cap.capId}
                                                </Link>
                                            </td>
                                            <td className="data-deletion-builder-cell" title={builderName}>
                                                {builderName}
                                            </td>
                                            <td className="data-deletion-active-list">
                                                {activeOthers.length === 0 ? (
                                                    <span className="data-deletion-muted">None</span>
                                                ) : (
                                                    <ul>
                                                        {activeOthers.slice(0, 5).map((c) => (
                                                            <li key={c.capId} title={c.name || c.capId}>
                                                                <Link to={`/caps/${c.capId}`}>{c.name || c.capId}</Link>
                                                            </li>
                                                        ))}
                                                        {activeOthers.length > 5 && (
                                                            <li className="data-deletion-muted">
                                                                +{activeOthers.length - 5} more
                                                            </li>
                                                        )}
                                                    </ul>
                                                )}
                                            </td>
                                            <td>
                                                <div>{formatDate(lastHealth)}</div>
                                                {ageDays != null && (
                                                    <div className="data-deletion-age">{ageDays}d ago</div>
                                                )}
                                            </td>
                                            <td className="data-deletion-count-cell">
                                                {eventCounts == null || eventCounts.inS3 == null ? (
                                                    <span className="data-deletion-muted">—</span>
                                                ) : (
                                                    <div>{eventCounts.inS3.toLocaleString()}</div>
                                                )}
                                                {eventBreakdown && eventBreakdown.length > 0 && (
                                                    <div className="data-deletion-count-sub" title="Archived / Deleted">
                                                        {eventBreakdown.join(" · ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="data-deletion-count-cell">
                                                {imageCounts == null || imageCounts.inS3 == null ? (
                                                    <span className="data-deletion-muted">—</span>
                                                ) : (
                                                    <div>{imageCounts.inS3.toLocaleString()}</div>
                                                )}
                                                {imageBreakdown && imageBreakdown.length > 0 && (
                                                    <div className="data-deletion-count-sub" title="Archived / Deleted">
                                                        {imageBreakdown.join(" · ")}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="data-deletion-status-cell" title={deletionTooltip}>
                                                <Label bsStyle={isDelete ? "danger" : "warning"}>
                                                    {cap.status}
                                                </Label>
                                                {deletion?.status && (
                                                    <div className="data-deletion-status-extra">
                                                        <Label bsStyle={deletionStatusBsStyle(deletion.status)}>
                                                            {deletion.status}
                                                        </Label>
                                                        {(formatDateShort(deletion.completedAt) ||
                                                            formatDateShort(deletion.startedAt)) && (
                                                            <div className="data-deletion-age">
                                                                {formatDateShort(deletion.completedAt) ||
                                                                    formatDateShort(deletion.startedAt)}
                                                                {typeof deletion.archiveAmount === "number" &&
                                                                    deletion.archiveAmount > 0 &&
                                                                    ` · ${formatBytes(deletion.archiveAmount)}`}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="data-deletion-actions">
                                                <Button
                                                    bsSize="xsmall"
                                                    bsStyle="info"
                                                    disabled={!!countingCapId || isDelete}
                                                    onClick={() => handleFetchCounts(cap)}
                                                    title="Count IN_S3 events and images (can take 10–30+ seconds)"
                                                >
                                                    {isCounting ? (
                                                        <Glyphicon glyph="refresh" className="glyphicon-spin" />
                                                    ) : (
                                                        "Fetch counts"
                                                    )}
                                                </Button>
                                                <Button
                                                    bsSize="xsmall"
                                                    bsStyle="danger"
                                                    disabled={!!deletingCapId || isDelete}
                                                    onClick={() => handleMarkDelete(cap)}
                                                    title="Mark cap status as DELETE"
                                                >
                                                    {isDeleting ? (
                                                        <Glyphicon glyph="refresh" className="glyphicon-spin" />
                                                    ) : isDelete ? (
                                                        "Deleted"
                                                    ) : (
                                                        "Mark DELETE"
                                                    )}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}
